import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useState } from "react";

import { cn } from "@/shared/lib";
import { Spinner } from "@/shared/ui/spinner";

import { formatGameTime } from "../../format/formatGameTime";
import type { Analysis } from "../../model/Analysis";
import { PREPARATION_STEPS } from "./PREPARATION_STEPS";
import { stepIndexOf } from "./stepIndexOf";
import { useNow } from "./useNow";

// Шаг дольше этого — «дольше обычного» с объяснением, а не тишина.
const SLOW_STEP_MS = 30_000;

const TITLE = msg({ id: "analysis.progress.title", message: "Preparing the analysis" });
const PROGRESS_LABEL = msg({ id: "analysis.progress.label", message: "Analysis progress" });
const ELAPSED = msg({ id: "analysis.progress.elapsed", message: "{time} elapsed" });
const STEP_TIME = msg({
  id: "analysis.progress.step_time",
  message: "{time} · usually up to {typical}",
});
const LEAVE_OK = msg({
  id: "analysis.progress.leave_ok",
  message:
    "You can leave this page: the analysis finishes on its own and the match in your list turns into “Open analysis”.",
});
const SLOW = msg({
  id: "analysis.progress.slow",
  message:
    "Taking longer than usual: Deadlock data is responding slowly right now. The analysis keeps going, you don't need to do anything.",
});
const WAITING = msg({
  id: "analysis.progress.waiting_for_match",
  message: "The match hasn't appeared in Deadlock data yet. This usually takes a few minutes.",
});
const WAITING_RETRY = msg({
  id: "analysis.progress.waiting_retry_at",
  message: "We'll check again at {time}.",
});
const STEP_DONE = msg({ id: "analysis.progress.step_done", message: "done" });
const STEP_CURRENT = msg({ id: "analysis.progress.step_current", message: "in progress" });

type AnalysisProgressProps = {
  analysis: Pick<Analysis, "status" | "next_retry_at" | "created_at" | "updated_at">;
};

// Разбор готовится (design/prototype/LoadingStates.dc.html): шаги конвейера, у текущего —
// спиннер, бегущий отрезок полосы, сколько прошло на шаге и сколько он обычно занимает;
// дольше обычного — объяснение. Шаги двигают события `analysis.progress`, без соединения —
// опрос; начало шага — момент, когда экран увидел новый статус (первый — `updated_at`).
export const AnalysisProgress = ({ analysis }: AnalysisProgressProps) => {
  const { _, i18n } = useLingui();
  const now = useNow();
  const [stepTimer, setStepTimer] = useState(() => ({
    status: analysis.status,
    since: Math.min(Date.parse(analysis.updated_at), now),
  }));
  if (stepTimer.status !== analysis.status) {
    setStepTimer({ status: analysis.status, since: now });
  }
  const current = stepIndexOf(analysis.status);
  const percent = Math.round((current / PREPARATION_STEPS.length) * 100);
  const waiting = analysis.status === "waiting_for_match";
  const elapsedMs = Math.max(0, now - Date.parse(analysis.created_at));
  const stepMs = Math.max(0, now - stepTimer.since);
  const slow = !waiting && stepMs > SLOW_STEP_MS;
  const retryAt =
    analysis.next_retry_at === null
      ? null
      : new Intl.DateTimeFormat(i18n.locale, { hour: "2-digit", minute: "2-digit" }).format(
          new Date(analysis.next_retry_at),
        );

  return (
    <section
      data-analysis-state={analysis.status}
      aria-labelledby="analysis-progress-title"
      aria-busy="true"
      className="flex flex-col gap-4 rounded-xl border border-border bg-surface px-4.5 py-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="analysis-progress-title" className="font-display text-2xl font-bold">
          {_(TITLE)}
        </h2>
        <span className="font-mono text-sm text-muted">
          {_({ ...ELAPSED, values: { time: formatGameTime(elapsedMs / 1000) } })}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={_(PROGRESS_LABEL)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="flex h-2 gap-1"
      >
        {PREPARATION_STEPS.map((step, index) => (
          <span
            key={step.status}
            className={cn(
              "flex-1 rounded-full",
              index < current && "bg-good",
              index === current &&
                "animate-stripes bg-accent bg-stripes motion-reduce:animate-none",
              index > current && "bg-surface-raised",
            )}
          />
        ))}
      </div>
      <ol className="flex flex-col gap-2">
        {PREPARATION_STEPS.map((step, index) => {
          const state = index < current ? "done" : index === current ? "current" : "pending";
          return (
            <li
              key={step.status}
              data-step={step.status}
              data-step-state={state}
              aria-current={state === "current" ? "step" : undefined}
              className={cn(
                "flex items-center gap-2.5 text-sm",
                state === "pending" ? "text-subtle" : "text-foreground",
              )}
            >
              {state === "current" ? (
                <Spinner className="text-accent" />
              ) : (
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-2.5 shrink-0 rounded-full",
                    state === "done" ? "bg-good" : "bg-border",
                  )}
                />
              )}
              <span className={cn(state === "current" && "font-semibold")}>{_(step.label)}</span>
              {state !== "pending" && (
                <span className="sr-only"> — {_(state === "done" ? STEP_DONE : STEP_CURRENT)}</span>
              )}
              {state === "current" && !waiting && (
                <span
                  className={cn("ml-auto font-mono text-xs", slow ? "text-warn" : "text-accent")}
                >
                  {_({
                    ...STEP_TIME,
                    values: {
                      time: formatGameTime(stepMs / 1000),
                      typical: formatGameTime(step.typicalS),
                    },
                  })}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {waiting ? (
        <p
          role="status"
          className="rounded-lg bg-warn/10 px-3.5 py-2.5 text-sm text-foreground-soft"
        >
          {_(WAITING)}
          {retryAt !== null && <> {_({ ...WAITING_RETRY, values: { time: retryAt } })}</>}
        </p>
      ) : slow ? (
        <p
          role="status"
          className="rounded-lg border border-border-strong bg-warn/10 px-3.5 py-2.5 text-sm text-accent-strong"
        >
          {_(SLOW)}
        </p>
      ) : (
        <p className="text-[13px] text-subtle">{_(LEAVE_OK)}</p>
      )}
    </section>
  );
};
